import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/auth';
import { createEnvelope } from '../lib/api';
import { friendlyError } from '../lib/errors';
import CategoryPicker from '../components/CategoryPicker';
import { getCategory } from '@beckersign/shared';

interface Row { id: string; name: string; status: string; createdAt: string; eventSeq: number }

export default function EnvelopeList({ onOpen }: { onOpen: (id: string) => void }) {
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [ack, setAck] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'envelopes'),
      where('ownerUid', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(
      q,
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Row))),
      (e) => setErr(friendlyError(e, 'Could not load envelopes.'))
    );
  }, [user]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !canCreate) return;
    setBusy(true); setErr(null);
    try {
      const res = await createEnvelope({
        name: name.trim(), documentCategory: category, acknowledgedWarnings: ack,
      });
      setName(''); setCategory(''); setAck([]);
      onOpen(res.data.envelopeId);
    } catch (e) {
      setErr(friendlyError(e, 'Could not create envelope.'));
    } finally { setBusy(false); }
  }

  const cat = getCategory(category);
  const blocked = cat?.eligibility === 'block';
  const needsAck = cat?.eligibility === 'warn' && !ack.includes(cat.id);
  const canCreate = Boolean(name.trim()) && Boolean(cat) && !blocked && !needsAck;

  return (
    <>
      <h2>New envelope</h2>
      <form onSubmit={create}>
        <label style={{ display: 'block', marginBottom: '.75rem' }}>
          <span className="muted">Envelope name</span>
          <input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Roofing contract — 118 Maple"
            maxLength={200} required
            style={{ width: '100%', padding: '.55rem', minHeight: 44, marginTop: '.25rem' }}
          />
        </label>

        <CategoryPicker
          value={category} onChange={setCategory}
          acknowledged={ack} onAcknowledge={setAck}
        />

        <button
          className="primary" style={{ marginTop: '1rem' }}
          disabled={busy || !canCreate}
          title={blocked ? 'This document type cannot be signed electronically.' : undefined}
        >
          {busy ? 'Creating…' : 'Create draft'}
        </button>
      </form>
      {err && <div className="alert" role="alert" style={{ marginTop: '1rem' }}>{err}</div>}

      <h2>Envelopes</h2>
      {rows.length === 0 ? (
        <p className="muted">None yet.</p>
      ) : (
        <table>
          <thead><tr><th>Name</th><th>Status</th><th>Events</th><th>Created</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td><code>{r.status}</code></td>
                <td>{r.eventSeq ?? 0}</td>
                <td className="muted">{new Date(r.createdAt).toLocaleString()}</td>
                <td><button onClick={() => onOpen(r.id)}>Open</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
